// WU-2.1 — banner state: login fetch (source of truth) + live WS merge
// (acceleration). Ref-stable WS registration copies useEscalationAlerts.
// User-targeted frames ride the tenant-wide queue:<tenant> channel by design
// (spec §4) — isAnnouncementForMe() is the client-side audience gate.
import { useCallback, useEffect, useRef, useState } from "react";
import { isAnnouncementActive, isAnnouncementForViewer } from "@capture/shared";
import type { WsClient } from "@/lib/ws-client";
import { useAuth } from "@/hooks/useAuth";
import { announcementsApi, type AnnouncementRecord } from "../api/client";

/** Sweep cadence for dropping locally-expired announcements. */
const EXPIRY_TICK_MS = 60_000;

type Viewer = { userId: string; permissions: string[]; teamIds?: readonly string[] } | null;

/** Audience gate — the shared predicate the api's fetch path also enforces. */
export function isAnnouncementForMe(a: AnnouncementRecord, user: Viewer): boolean {
  return isAnnouncementForViewer(a, user);
}

/** Local expiry check (cleared removal arrives via WS; server never sends cleared rows). */
function notExpired(a: AnnouncementRecord, now: number): boolean {
  return isAnnouncementActive({ clearedAt: a.clearedAt, expiresAt: a.expiresAt }, now);
}

export function useAnnouncements(wsClient: WsClient | null): {
  announcements: AnnouncementRecord[];
  dismiss: (id: string) => Promise<void>;
} {
  const { user } = useAuth();
  const [all, setAll] = useState<AnnouncementRecord[]>([]);
  const [now, setNow] = useState(() => Date.now());

  // Server-resolved team context (spec §6/§9) — the set of teams whose
  // subtree contains this viewer, as returned by GET /announcements. Starts
  // empty so a team-audience WS frame arriving before the fetch resolves
  // fails closed (see isAnnouncementForViewer's "team" case).
  const viewerTeamIdsRef = useRef<readonly string[]>([]);

  // Ref keeps the WS handler reading the CURRENT user without re-subscribing.
  // Rebuilt every render (not just in an effect) so it always reflects the
  // latest user AND the latest viewerTeamIdsRef — the fetch handler below
  // updates viewerTeamIdsRef then calls setAll, whose re-render refreshes
  // this assignment before any subsequent WS frame can be handled.
  const userRef = useRef<Viewer>(null);
  userRef.current = user
    ? { userId: user.userId, permissions: user.permissions, teamIds: viewerTeamIdsRef.current }
    : null;

  // B5 (ff-web) — tombstone for ids cleared via WS WHILE the initial fetch
  // (below) is still in flight. The fetch is a single round-trip that may
  // have started before the clear landed, so its response can still carry
  // the now-cleared row; without this, merging that response back in would
  // resurrect it. `fetchResolvedRef` gates when the WS handler bothers
  // recording tombstones (irrelevant once the initial fetch is done).
  const clearedDuringFetchRef = useRef<Set<string>>(new Set());
  const fetchResolvedRef = useRef(false);

  // Initial fetch — server already filters visibility + dismissals.
  // MERGE (not replace) on resolution: a live WS frame can land while the
  // HTTP round-trip is in flight, and this effect never re-runs — a
  // destructive setAll would silently drop it. Fetched entries are the base
  // (server truth); WS-arrived entries not in the fetched set are kept.
  // Fetched entries tombstoned by a WS clear during the same window are
  // dropped rather than resurrected.
  useEffect(() => {
    let cancelled = false;
    announcementsApi
      .listMine()
      .then((res) => {
        if (cancelled) return;
        fetchResolvedRef.current = true;
        viewerTeamIdsRef.current = res.viewerTeamIds ?? [];
        setAll((prev) => {
          const fetchedIds = new Set(res.announcements.map((a) => a.id));
          const wsOnly = prev.filter((a) => !fetchedIds.has(a.id));
          const fetched = res.announcements.filter(
            (a) => !clearedDuringFetchRef.current.has(a.id),
          );
          return [...wsOnly, ...fetched];
        });
      })
      .catch(() => {
        /* banner is best-effort; next login/refresh retries */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Live merge + clear.
  useEffect(() => {
    if (!wsClient) return;
    const offNew = wsClient.onPayload("announcement", (payload) => {
      const a = payload as AnnouncementRecord;
      if (!a?.id) return;
      if (!isAnnouncementForMe(a, userRef.current)) return;
      setAll((prev) => (prev.some((x) => x.id === a.id) ? prev : [a, ...prev]));
    });
    const offCleared = wsClient.onPayload("announcement_cleared", (payload) => {
      const id = (payload as { id?: string })?.id;
      if (!id) return;
      if (!fetchResolvedRef.current) clearedDuringFetchRef.current.add(id);
      setAll((prev) => prev.filter((a) => a.id !== id));
    });
    return () => {
      offNew();
      offCleared();
    };
  }, [wsClient]);

  // Local expiry sweep.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), EXPIRY_TICK_MS);
    return () => clearInterval(t);
  }, []);

  const dismiss = useCallback(async (id: string) => {
    await announcementsApi.dismiss(id); // server first — dismissal must survive reloads
    setAll((prev) => prev.filter((a) => a.id !== id));
  }, []);

  return { announcements: all.filter((a) => notExpired(a, now)), dismiss };
}
